// iter-298 — Financial Movement tab (3-card layout).
//
// The merchant sees:
//   * 3 summary cards:
//       1. Total paid by customers  — every non-cancelled order (reserved OR
//          credited). Final price × qty.
//       2. Pending in wallet         — reserved payable for orders that are
//          not yet `delivered`. Base × qty.
//       3. Credited to wallet        — merchant payable for `delivered` orders
//          that have been promoted into the wallet balance. Base × qty.
//   * A per-line ledger with: date, order, receipt chip, product,
//     base, commission, admin_fee, final, qty, status, and a wallet-state chip.
//
// The commission split is still merchant-only — the customer never sees it.

import { useState, useEffect, useMemo } from 'react';
import {
  Wallet, Clock, CheckCircle2, RefreshCw, FileWarning,
  ArrowDownUp, Loader2, Receipt,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const fmtAED = (v, lang) => {
  const n = Number(v || 0);
  return `${n.toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${lang === 'ar' ? 'د.إ' : 'AED'}`;
};

const fmtDate = (iso, lang) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-US', {
      year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const StatusPill = ({ status, lang, isDarkMode }) => {
  const map = {
    delivered:  { bg: 'bg-green-100 text-green-700',   dark: 'bg-green-500/20 text-green-400',   ar: 'تم التوصيل', en: 'Delivered' },
    shipped:    { bg: 'bg-indigo-100 text-indigo-700', dark: 'bg-indigo-500/20 text-indigo-400', ar: 'تم الشحن',   en: 'Shipped' },
    processing: { bg: 'bg-purple-100 text-purple-700', dark: 'bg-purple-500/20 text-purple-400', ar: 'قيد التجهيز', en: 'Processing' },
    confirmed:  { bg: 'bg-blue-100 text-blue-700',     dark: 'bg-blue-500/20 text-blue-400',     ar: 'مؤكد',       en: 'Confirmed' },
    pending:    { bg: 'bg-amber-100 text-amber-700',   dark: 'bg-amber-500/20 text-amber-400',   ar: 'في الانتظار', en: 'Pending' },
    cancelled:  { bg: 'bg-red-100 text-red-700',       dark: 'bg-red-500/20 text-red-400',       ar: 'ملغي',       en: 'Cancelled' },
  };
  const cfg = map[status] || map.pending;
  const cls = isDarkMode ? cfg.dark : cfg.bg;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>
      {lang === 'ar' ? cfg.ar : cfg.en}
    </span>
  );
};

// iter-298 — wallet state chip so the merchant can tell at a glance whether
// a line already landed in their wallet or is still reserved.
const WalletChip = ({ state, lang, isDarkMode }) => {
  const map = {
    credited: {
      ar: 'في المحفظة', en: 'In wallet',
      bg: isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700',
    },
    pending: {
      ar: 'معلّق', en: 'Reserved',
      bg: isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700',
    },
    cancelled: {
      ar: '—', en: '—',
      bg: isDarkMode ? 'bg-slate-500/20 text-slate-300' : 'bg-slate-100 text-slate-600',
    },
  };
  const cfg = map[state] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.bg}`}>
      {lang === 'ar' ? cfg.ar : cfg.en}
    </span>
  );
};

const SummaryCard = ({ icon: Icon, label, hint, value, tone, isDarkMode, testId }) => {
  const tones = {
    slate:   { bg: isDarkMode ? 'bg-slate-500/10 border-slate-400/20'       : 'bg-slate-50 border-slate-100',     fg: 'text-slate-700' },
    amber:   { bg: isDarkMode ? 'bg-amber-500/10 border-amber-400/30'       : 'bg-amber-50 border-amber-200',     fg: 'text-amber-600' },
    emerald: { bg: isDarkMode ? 'bg-emerald-500/10 border-emerald-400/30'   : 'bg-emerald-50 border-emerald-200', fg: 'text-emerald-600' },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className={`rounded-2xl border p-4 ${t.bg}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <p className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{label}</p>
        <Icon className={`h-5 w-5 ${t.fg}`} />
      </div>
      <p className={`mt-2 text-2xl font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value}</p>
      {hint && (
        <p className={`mt-1 text-[11px] leading-snug ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{hint}</p>
      )}
    </div>
  );
};

export function FinancialMovementTab({ storeId, language, isDarkMode, BACKEND_URL }) {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('business_token') : null;

  const fetchAll = async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    setError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [sRes, mRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/merchant/stores/${encodeURIComponent(storeId)}/financial-summary`, { headers, timeout: 15000 }),
        axios.get(`${BACKEND_URL}/api/merchant/stores/${encodeURIComponent(storeId)}/financial-movement?limit=200`, { headers, timeout: 15000 }),
      ]);
      setSummary(sRes.data);
      setRows(mRes.data?.rows || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Network error';
      setError(String(msg));
      if (silent) toast.error(language === 'ar' ? 'تعذّر تحديث السجل' : 'Could not refresh ledger');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { if (storeId && token) fetchAll(); /* eslint-disable-next-line */ }, [storeId]);

  const totals = useMemo(() => ({
    paid:     summary?.total_paid_by_customers ?? 0,
    pending:  summary?.pending_wallet ?? 0,
    credited: summary?.wallet_credited ?? 0,
    pendingOrders:  summary?.pending_orders_count ?? 0,
    creditedOrders: summary?.credited_orders_count ?? 0,
    lines:    summary?.lines_count ?? 0,
  }), [summary]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="fin-loading">
        <Loader2 className={`h-8 w-8 animate-spin ${isDarkMode ? 'text-blue-400' : 'text-[#1E9BD9]'}`} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="financial-movement-tab">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className={`text-xl font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
            <ArrowDownUp className="h-5 w-5 text-[#1E9BD9]" />
            {language === 'ar' ? 'الحركة المالية' : 'Financial Movement'}
          </h2>
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {language === 'ar'
              ? 'ملخّص المدفوعات ومتى تصل أموالك إلى المحفظة — يُحوَّل مبلغ الطلب تلقائياً للمحفظة عند «تم التوصيل».'
              : 'Payments summary and when each order lands in your wallet — amounts auto-credit on "Delivered".'}
          </p>
        </div>
        <button
          onClick={() => fetchAll(true)}
          disabled={refreshing}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white hover:bg-white/5' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
          data-testid="fin-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {language === 'ar' ? 'تحديث' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 text-red-700 border border-red-100 text-sm" data-testid="fin-error">
          <FileWarning className="h-4 w-4 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* iter-298 — Three summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SummaryCard
          icon={Wallet}
          tone="slate"
          label={language === 'ar' ? 'إجمالي ما دفعه العملاء' : 'Total paid by customers'}
          hint={language === 'ar' ? 'مجموع ما حصّلناه من العملاء (بدون الطلبات الملغاة).' : 'All non-cancelled orders.'}
          value={fmtAED(totals.paid, language)}
          isDarkMode={isDarkMode}
          testId="fin-card-paid"
        />
        <SummaryCard
          icon={Clock}
          tone="amber"
          label={language === 'ar' ? 'المبالغ المعلّقة' : 'Pending (reserved)'}
          hint={
            language === 'ar'
              ? `${totals.pendingOrders} طلب لم يُسلَّم بعد — سيُرحَّل تلقائياً للمحفظة عند «تم التوصيل».`
              : `${totals.pendingOrders} order(s) not delivered yet — auto-credited on "Delivered".`
          }
          value={fmtAED(totals.pending, language)}
          isDarkMode={isDarkMode}
          testId="fin-card-pending"
        />
        <SummaryCard
          icon={CheckCircle2}
          tone="emerald"
          label={language === 'ar' ? 'المبالغ في المحفظة' : 'Credited to wallet'}
          hint={
            language === 'ar'
              ? `${totals.creditedOrders} طلب تم تسليمه ودخل مبلغه إلى محفظتك.`
              : `${totals.creditedOrders} delivered order(s) sitting in your wallet.`
          }
          value={fmtAED(totals.credited, language)}
          isDarkMode={isDarkMode}
          testId="fin-card-credited"
        />
      </div>

      <div className={`flex items-center gap-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <span><strong>{rows.length}</strong> {language === 'ar' ? 'سطر في السجل' : 'rows in ledger'}</span>
      </div>

      {/* Ledger table */}
      <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'border-white/10 bg-[#0F172A]' : 'border-gray-100 bg-white'}`}>
        {rows.length === 0 ? (
          <div className="text-center py-16" data-testid="fin-empty">
            <ArrowDownUp className={`h-10 w-10 mx-auto mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {language === 'ar' ? 'لا توجد حركات مالية بعد.' : 'No financial movements yet.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fin-table">
              <thead className={isDarkMode ? 'bg-white/5' : 'bg-gray-50'}>
                <tr>
                  <Th>{language === 'ar' ? 'التاريخ' : 'Date'}</Th>
                  <Th>{language === 'ar' ? 'الطلب / الإيصال' : 'Order / Receipt'}</Th>
                  <Th>{language === 'ar' ? 'المنتج' : 'Product'}</Th>
                  <Th align="center">{language === 'ar' ? 'الكمية' : 'Qty'}</Th>
                  <Th align="end">{language === 'ar' ? 'السعر الأصلي' : 'Base'}</Th>
                  <Th align="end">{language === 'ar' ? 'العمولة' : 'Commission'}</Th>
                  <Th align="end">{language === 'ar' ? 'الرسوم' : 'Admin fee'}</Th>
                  <Th align="end">{language === 'ar' ? 'الإجمالي' : 'Final'}</Th>
                  <Th align="center">{language === 'ar' ? 'الحالة' : 'Status'}</Th>
                  <Th align="center">{language === 'ar' ? 'المحفظة' : 'Wallet'}</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr
                    key={`${row.order_id}-${row.product_id}-${idx}`}
                    className={`border-t ${isDarkMode ? 'border-white/5' : 'border-gray-100'}`}
                    data-testid={`fin-row-${idx}`}
                  >
                    <Td muted>{fmtDate(row.created_at, language)}</Td>
                    <Td>
                      <div className="flex flex-col gap-0.5">
                        <span className="font-mono text-[12px] font-bold">#{row.order_id}</span>
                        {row.receipt_number && (
                          <span
                            className={`inline-flex items-center gap-1 font-mono text-[10px] ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}
                            data-testid={`fin-receipt-${idx}`}
                          >
                            <Receipt className="h-3 w-3" />
                            {row.receipt_number}
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td>
                      <div className="min-w-0 max-w-[220px] truncate font-medium">
                        {language === 'ar' ? (row.product_name || row.product_name_en) : (row.product_name_en || row.product_name)}
                      </div>
                    </Td>
                    <Td align="center">{row.quantity}</Td>
                    <Td align="end" strong>{fmtAED(row.line_total_base, language)}</Td>
                    <Td align="end" tone="blue">{fmtAED(row.line_total_commission, language)}</Td>
                    <Td align="end" tone="amber">{fmtAED(row.line_total_admin_fee, language)}</Td>
                    <Td align="end" strong>{fmtAED(row.line_total_final, language)}</Td>
                    <Td align="center"><StatusPill status={row.status} lang={language} isDarkMode={isDarkMode} /></Td>
                    <Td align="center"><WalletChip state={row.payment_state} lang={language} isDarkMode={isDarkMode} /></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className={`text-[11px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        {language === 'ar'
          ? '💡 ملاحظة: مبلغ الطلب يُحوَّل تلقائياً إلى محفظتك عند تحديث حالته إلى «تم التوصيل». العمولة والرسوم الإدارية مستحقّة للمنصة ولا تظهر للعميل.'
          : '💡 Note: an order\'s payable auto-credits your wallet when its status becomes "Delivered". Commission + admin fees accrue to the platform and are hidden from customers.'}
      </p>
    </div>
  );
}

function Th({ children, align = 'start' }) {
  const cls = align === 'end' ? 'text-right' : align === 'center' ? 'text-center' : 'text-start';
  return <th className={`px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-500 ${cls}`}>{children}</th>;
}

function Td({ children, align = 'start', mono, strong, muted, tone }) {
  const cls = align === 'end' ? 'text-right' : align === 'center' ? 'text-center' : 'text-start';
  const style = [
    'px-3 py-2 whitespace-nowrap',
    mono ? 'font-mono text-[12px]' : '',
    strong ? 'font-bold' : '',
    muted ? 'text-gray-500 text-[12px]' : '',
    tone === 'blue' ? 'text-blue-600' : '',
    tone === 'amber' ? 'text-amber-600' : '',
  ].join(' ');
  return <td className={`${cls} ${style}`}>{children}</td>;
}

export default FinancialMovementTab;
